import React, { useState, useCallback } from 'react';
import clsx from 'clsx';
import { useDispatch } from 'react-redux';
import { bindActionCreators } from 'redux';

import { pushSiteNotification as action } from 'containers/App/actions';

import Avatar from 'components/Avatar';
import { MobileSm, DefaultSm } from 'components/MediaQueries';
import { animateOverflow, animateWidth } from 'utils/animations';

export const LeadContactCell = ({ leadItem, visibleColumns }) => {
  const {
    contactName,
    contactFirstName,
    contactLastName,
    contactId,
    contactEmail,
    contactNumber,
    contactCompanyName,
  } = leadItem;
  const [hovered, setHovered] = useState(false);
  const [cellWidth, setCellWidth] = useState(0);
  const displayName = contactName || (contactLastName || '').trim();
  const avatarImages = importAll(require.context('../../assets/images/leads/nature', false, /\.svg/));
  // #TODO: New webpack build exports the 5 images twice, so need to use a magic number to get
  // avatar images to work correctly until the webpack build can be inspected.
  const avatarImageId = contactId % 5 + 1;
  const avatarName =  `${contactFirstName?.charAt(0)}${contactLastName?.charAt(0)}`.toUpperCase();

  const dispatch = useDispatch();
  const { pushNotification } = bindActionCreators({ pushNotification: action }, dispatch);

  const checkOverflow = useCallback((el) => {
    if (el?.scrollWidth > el?.clientWidth && !el?.classList.contains('overflowing')) {
      el?.classList.add('overflowing');
      if (el?.scrollWidth > cellWidth) {
        setCellWidth(el?.scrollWidth);
      }
    }
    else {
      el?.classList.remove('overflowing');
    }
  }, [hovered]);

  const animateContact = useCallback((el) => {
    // Need to call twice and wait a little for state to change.
    const color = '#fff';
    setTimeout(() => {
      if (hovered) {
        animateOverflow(el, 'animate', { background: color });
      }
    }, 100);
    animateOverflow(el, hovered ? 'init' : 'destroy', { background: color });
  }, [hovered]);

  const expandContact = useCallback((el) => {
    if (hovered && el && cellWidth) {
      // Magic number of max width for contact details.
      const newWidth = cellWidth + el.clientWidth - 180;
      // Add some extra buffer.
      animateWidth(el, newWidth, 250);
    }
    else if (el) animateWidth(el, el.clientWidth, 100);
  }, [hovered]);

  const copy = (string) => {
    navigator.clipboard.writeText(string);
  };

  const copyButton = (
    <button
      className="button--unstyled button--copy"
      title="copy"
    >
      <span className="fal fa-copy" />
    </button>
  );

  return (
    <React.Fragment key="table-item-contact">
      <DefaultSm>
        <td
          className={clsx('table-item', hovered && 'hovered')}
          onMouseEnter={() => setHovered(true)}
          onMouseLeave={() => setHovered(false)}
        >
          <div className="lead-record__contact inline-avatar">
            <div
              ref={expandContact}
              className="lead-record__animated-background"
            />
            <div className="inline-avatar__image">
              <Avatar
                photo={avatarImages[`nature-${avatarImageId}.svg`]}
                text={avatarName}
                size={85}
                borderSize={5}
                className="avatar--transparent avatar--dark"
              />
            </div>
            <div
              ref={animateContact}
              className="lead-contact-item inline-avatar__info"
            >
              <p
                ref={checkOverflow}
                className={clsx('lead-record__contact-detail', 'lead-record__name')}
              >
                {displayName}
              </p>
              {visibleColumns.includes('contactCompanyName') && (
                <p
                  ref={checkOverflow}
                  className={clsx('lead-record__contact-detail', 'lead-record__company')}
                >
                  {contactCompanyName}
                </p>
              )}
              <p
                ref={checkOverflow}
                className={clsx('lead-record__contact-detail', 'lead-record__email')}
              >
                {contactEmail}
                {contactEmail !== '' &&
                  React.cloneElement(copyButton, {
                    onClick: (e) => {
                      e.stopPropagation();
                      pushNotification({
                        message: 'Email copied to clipboard.',
                        time: 3000,
                      });
                      copy(contactEmail);
                    },
                    'aria-label': 'Copy email address',
                  })
                }
              </p>
              <p className="lead-record__contact-detail lead-record__phone">
                {contactNumber}
                {contactNumber !== '' && React.cloneElement(copyButton, {
                    onClick: (e) => {
                      e.stopPropagation();
                      pushNotification({
                        message: 'Phone number copied to clipboard.',
                        time: 3000,
                      });
                      copy(contactNumber);
                    },
                    'aria-label': 'Copy phone number',
                  })
                }
              </p>
            </div>
          </div>
        </td>
      </DefaultSm>
      <MobileSm>
        <div className="lead-record__contact d-flex">
          <div className="mr-3">
            <Avatar
              photo={avatarImages[`nature-${avatarImageId}.svg`]}
              text={avatarName}
              size={60}
              borderSize={3}
              className="avatar--transparent avatar--dark"
            />
          </div>
          <div className="w-100 lead-contact-item">
            <p className="lead-record__contact-detail lead-record__name">{displayName}</p>
            <p className="lead-record__contact-detail lead-record__email">{contactEmail}</p>
            <p className="lead-record__contact-detail lead-record__phone">{contactNumber}</p>
          </div>
        </div>
      </MobileSm>
    </React.Fragment>
  );
};

function importAll(r) {
  const images = {};
  r.keys().forEach(item => {
    images[item.replace('./', '')] = r(item);
  });
  return images;
}
